/**
 * EnemyVisualScale (BL-285) — the enemy body PRESENCE mapping.
 *
 * The authored `enemies.json` radii (5 … 22 on a 48 px cell) were tuned on a
 * desktop canvas. On an iPhone the logical 960×672 board is letterboxed to
 * roughly three quarters of that, so a swarm cell (r6 → 12 px) landed at ~9
 * device-independent px: a coloured dot. The BL-51 silhouettes — the whole
 * point of "malware visualised" — were unreadable at exactly the sizes where
 * the player most needs to tell one threat from another.
 *
 * This module is the ONE place that turns an authored radius into the radius we
 * actually draw. It is deliberately NOT a change to `enemies.json`, because the
 * authored value is also a **tier signal**: `DensityGlow.focalWeight` keys the
 * halo-damping tie-break on `radius >= 13`. Re-authoring the data would have
 * silently re-tiered heavy trash. The sim never reads either value.
 *
 * The mapping is affine with a cap:
 *
 *   visual = min(MAX, BASE + authored × GAIN)
 *
 * The positive intercept is the design: it lifts the SMALL bodies hardest (a
 * splitling nearly doubles, +90%) and the big ones least (+8% at the cap), so
 * the boost lands where legibility was actually failing while the apex stays
 * the apex. The cap holds the final boss at ~27 px — a hair over the 24 px
 * half-cell, so it dominates its tile without reaching into a neighbouring
 * tower's socket. Everything below `boss` stays strictly inside its own cell.
 *
 * Pure and render-only: no DOM, no clock, no sim coupling.
 */

/** Radius added to every body — the term that rescues the small silhouettes. */
export const ENEMY_VISUAL_BASE = 4.0;
/** Slope on the authored radius: keeps the class ordering intact. */
export const ENEMY_VISUAL_GAIN = 1.1;
/**
 * Hard ceiling (world px). 27 ≈ the 24 px half-cell plus a 3 px overhang: the
 * apex reads oversized, and no body can grow far enough to swallow the path or
 * an adjacent tower node.
 */
export const ENEMY_VISUAL_MAX = 27;

/**
 * Authored radius → the radius the renderer draws at.
 *
 * Guards non-positive input (a pooled slot mid-init) by returning 0, so a
 * caller can never build a body path from a negative radius.
 */
export function enemyVisualRadius(radius: number): number {
  if (!(radius > 0)) return 0;
  const r = ENEMY_VISUAL_BASE + radius * ENEMY_VISUAL_GAIN;
  return r > ENEMY_VISUAL_MAX ? ENEMY_VISUAL_MAX : r;
}
